import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { Send, SmartToy } from '@mui/icons-material';

import { CreditRiskAPI } from '../services/api';
import ChatMarkdown from './ChatMarkdown';
import {
  LoanApplicationData,
  PredictionResult,
  UnderwritingChatTurn,
  UnderwritingMode,
} from '../types';

interface UnderwritingChatProps {
  application: Partial<LoanApplicationData>;
  scorecard: Record<string, any> | null;
  loanDefault: PredictionResult | null;
  analysisKey: string;
}

const MODE_HINT: Record<Exclude<UnderwritingMode, 'expert'>, string> = {
  advisor: 'Interpreta el puntaje y separa perfil vs. solicitud.',
  counteroffer: 'Propone un monto y condiciones dentro del máximo.',
};

const UnderwritingChat: React.FC<UnderwritingChatProps> = ({
  application,
  scorecard,
  loanDefault,
  analysisKey,
}) => {
  const [mode, setMode] = useState<Exclude<UnderwritingMode, 'expert'>>('advisor');
  const [draft, setDraft] = useState('');
  const [messages, setMessages] = useState<UnderwritingChatTurn[]>([]);
  const [sources, setSources] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const briefedFor = useRef<string>('');

  const ready = Boolean(scorecard && loanDefault);

  useEffect(() => {
    setMessages([]);
    setSources([]);
    setError(null);
    setDraft('');
    briefedFor.current = '';
  }, [analysisKey]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  const payloadBase = useMemo(
    () => ({
      application: { ...application, new_client: true },
      scorecard: scorecard || undefined,
      loan_default: loanDefault || undefined,
    }),
    [application, scorecard, loanDefault]
  );

  const send = async (
    text: string,
    nextMode: UnderwritingMode = mode,
    history: UnderwritingChatTurn[] = messages,
    silent = false
  ) => {
    const content = text.trim();
    if (!content || !ready || loading) return;

    const userTurn: UnderwritingChatTurn = { role: 'user', content };
    const nextHistory = silent ? history : [...history, userTurn];
    if (!silent) {
      setMessages(nextHistory);
    }
    setDraft('');
    setLoading(true);
    setError(null);

    const response = await CreditRiskAPI.underwritingChat({
      message: content,
      mode: nextMode,
      expert_unlocked: false,
      history,
      ...payloadBase,
    });

    setLoading(false);
    if (!response.success || !response.data) {
      setError(response.error?.message || 'No se pudo consultar al asesor.');
      return;
    }

    setSources(response.data.sources || []);
    setMessages([
      ...nextHistory,
      { role: 'assistant', content: response.data.reply },
    ]);
  };

  useEffect(() => {
    if (!ready || !analysisKey || briefedFor.current === analysisKey) return;
    briefedFor.current = analysisKey;
    send(
      'Resume este caso para el oficial. Separa perfil vs solicitud y di qué harías.',
      'advisor',
      [],
      true
    );
    // First briefing only; later sends go through the form.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, analysisKey]);

  return (
    <Paper sx={{ p: 3, mt: 3 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} justifyContent="space-between" sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h6" color="primary">
            <SmartToy sx={{ mr: 1, verticalAlign: 'middle' }} />
            Asesor de suscripción
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {ready
              ? MODE_HINT[mode]
              : 'Ejecuta el análisis para abrir el chat sobre este expediente.'}
          </Typography>
        </Box>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={mode}
          onChange={(_event, value: Exclude<UnderwritingMode, 'expert'> | null) => {
            if (value) setMode(value);
          }}
          disabled={!ready}
        >
          <ToggleButton value="advisor">Asesor</ToggleButton>
          <ToggleButton value="counteroffer">Contrapropuesta</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      <Box
        sx={{
          height: 340,
          overflowY: 'auto',
          bgcolor: '#fafafa',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          p: 2,
          mb: 2,
        }}
      >
        {!ready && (
          <Typography variant="body2" color="text.secondary">
            Cuando tengas puntaje y PD, el asesor resume el caso solo.
          </Typography>
        )}
        {messages.map((turn, index) => {
          const mine = turn.role === 'user';
          return (
            <Box
              key={`${turn.role}-${index}`}
              sx={{
                display: 'flex',
                justifyContent: mine ? 'flex-end' : 'flex-start',
                mb: 1.5,
              }}
            >
              <Box
                sx={{
                  maxWidth: '85%',
                  px: 1.5,
                  py: 1,
                  borderRadius: 2,
                  bgcolor: mine ? 'primary.main' : 'white',
                  color: mine ? 'white' : 'text.primary',
                  border: mine ? 'none' : '1px solid',
                  borderColor: 'divider',
                  overflowX: 'auto',
                }}
              >
                <Typography variant="caption" sx={{ opacity: 0.75, display: 'block', mb: 0.5 }}>
                  {mine ? 'Oficial' : 'Asesor'}
                </Typography>
                {mine ? (
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {turn.content}
                  </Typography>
                ) : (
                    <ChatMarkdown text={turn.content} />
                )}
              </Box>
            </Box>
          );
        })}
        {loading && (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ color: 'text.secondary' }}>
            <CircularProgress size={16} />
            <Typography variant="caption">El asesor está leyendo el expediente…</Typography>
          </Stack>
        )}
        <div ref={bottomRef} />
      </Box>

      {sources.length > 0 && (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
          {sources.map((source) => (
            <Chip key={source} size="small" label={source} variant="outlined" />
          ))}
        </Stack>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>
      )}

      <Stack direction="row" spacing={1}>
        <TextField
          fullWidth
          size="small"
          placeholder={
            ready
              ? 'Pregunta sobre el puntaje, el monto o una condición…'
              : 'Primero analiza el crédito'
          }
          value={draft}
          disabled={!ready || loading}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && !event.shiftKey) {
              event.preventDefault();
              send(draft);
            }
          }}
        />
        <IconButton
          color="primary"
          onClick={() => send(draft)}
          disabled={!ready || loading || !draft.trim()}
        >
          <Send />
        </IconButton>
      </Stack>
    </Paper>
  );
};

export default UnderwritingChat;
